import { createContext, useContext, useReducer, useEffect } from 'react'
import { authAPI } from '../services/api'
import { storage } from '../utils/storage'
import i18n from '../i18n'
import { useAuthOperations } from '../hooks/useAuthOperations'
import { updateUserContext } from '../utils/analytics'

const AuthContext = createContext()

// Auth state
const initialState = {
  user: null,
  isLoading: true,
  isAuthenticated: false,
  error: null
}

// Auth reducer
const authReducer = (state, action) => {
  switch (action.type) {
    case 'AUTH_START':
      return {
        ...state,
        isLoading: true,
        error: null
      }
    case 'AUTH_SUCCESS':
      return {
        ...state,
        isLoading: false,
        isAuthenticated: true,
        user: action.payload.user,
        error: null
      }
    case 'AUTH_FAILURE':
      return {
        ...state,
        isLoading: false,
        isAuthenticated: false,
        user: null,
        error: action.payload
      }
    case 'LOGOUT':
      return {
        ...state,
        isLoading: false,
        isAuthenticated: false,
        user: null,
        error: null
      }
    case 'UPDATE_USER':
      return {
        ...state,
        user: { ...state.user, ...action.payload }
      }
    default:
      return state
  }
}

// Auth provider component
export const AuthProvider = ({ children }) => {
  const [state, dispatch] = useReducer(authReducer, initialState)
  
  // Import auth operations from custom hook
  const authOperations = useAuthOperations(dispatch)

  // Check for existing session on mount - but only if user was previously logged in
  useEffect(() => {
    const initializeAuth = async () => {
      // Check localStorage flag to see if user was logged in before
      const wasLoggedIn = storage.get('auth_session_active')
      
      if (!wasLoggedIn) {
        // User was never logged in, skip profile check
        dispatch({ type: 'AUTH_FAILURE', payload: null })
        return
      }
      
      // User might still be logged in via httpOnly cookie, check profile
      try {
        const response = await authAPI.getProfile()
        
        if (response.status === 401) {
          // Session expired
          storage.remove('auth_session_active')
          dispatch({ type: 'AUTH_FAILURE', payload: null })
          return
        }
        
        const user = response.data
        
        // Update i18n language from user preference if available
        if (user.language && user.language !== i18n.language) {
          i18n.changeLanguage(user.language)
        }
        
        dispatch({
          type: 'AUTH_SUCCESS',
          payload: { user }
        })
      } catch (error) {
        storage.remove('auth_session_active')
        dispatch({ type: 'AUTH_FAILURE', payload: null })
      }
    }

    initializeAuth()

    // Listen for cross-tab events
    const handleStorageChange = (e) => {
      if (e.key === 'auth_logout_event' && e.newValue) {
        dispatch({ type: 'LOGOUT' })
      }
      
      if (e.key === 'auth_login_event' && e.newValue) {
        initializeAuth()
      }
    }

    window.addEventListener('storage', handleStorageChange)
    
    return () => {
      window.removeEventListener('storage', handleStorageChange)
    }
  }, [])

  // Refresh profile when the tab becomes visible again (picks up admin-assigned track changes)
  useEffect(() => {
    if (!state.isAuthenticated) return
    const handleVisibility = () => {
      if (!document.hidden) {
        authOperations.refreshProfile()
      }
    }
    document.addEventListener('visibilitychange', handleVisibility)
    return () => document.removeEventListener('visibilitychange', handleVisibility)
  }, [state.isAuthenticated, authOperations])

  // Sync analytics user context whenever auth state changes
  useEffect(() => {
    if (state.isAuthenticated && state.user) {
      updateUserContext(state.user)
    } else {
      updateUserContext(null)
    }
  }, [state.isAuthenticated, state.user])

  // Context value
  const value = {
    ...state,
    ...authOperations
  }

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  )
}

// Custom hook to use auth context
export const useAuth = () => {
  const context = useContext(AuthContext)
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}

export default AuthContext